import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';
import 'moment/locale/es';

import { getHourMeterSummary } from '../../../../../slices/thunks';
import { getConsumptionByEstablishment } from '../../../../../slices/fuelSupply/thunk';
import EChartsBarChart from '../../../../../Components/Common/Charts/ECharts/EChartsBarChart';
import { ECHARTS_COLOR_PALETTE } from '../../../../../Components/Hooks/useColorArrayHooks';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

const getUniqueUnits = data => {
	if (!Array.isArray(data) || data.length === 0) return 'N/A';
	const uniqueUnits = new Set(
		data
			.map(item => item.unit_measurement_info?.name?.trim())
			.filter(Boolean)
			.map(unit => unit.charAt(0).toUpperCase() + unit.slice(1))
	);
	return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(', ') : 'N/A';
};

const prepareChartDataPowerECharts = (data, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const groupedData = data.reduce((acc, item) => {
		if (!item.register_date || !item.power_generation_source_info) return acc;
		const date = formatDate(item.register_date, filter);
		const generatorName =
			item.power_generation_source_info.name || 'Generador Desconocido';
		const serialNumber =
			item.power_generation_source_info.serial_number || 'SN-Desconocido';
		const fuelType = item.fuel_type_info?.name || 'Desconocido';
		const bargeName = item.feeding_barges_info?.name?.trim() || '';
		const generatorKey = bargeName
			? `${bargeName} - ${generatorName}`
			: generatorName;
		const key = `${date}-${generatorKey}`;
		const consumption = Number(item.consumption) || 0;

		if (!acc[key]) {
			acc[key] = {
				date,
				generatorKey,
				serialNumber,
				fuelType,
				bargeName,
				generatorName,
				consumption: 0,
			};
		}
		acc[key].consumption += consumption;
		return acc;
	}, {});

	const sortedData = Object.values(groupedData).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);
	const categories = [...new Set(sortedData.map(item => item.date))];
	const generators = [
		...new Set(sortedData.map(item => item.generatorKey)),
	].sort((a, b) => a.localeCompare(b));

	const series = generators.map(generatorKey => ({
		name: generatorKey,
		type: 'bar',
		barGap: 0,
		emphasis: {
			focus: 'series',
		},
		data: categories.map(date => {
			const record = sortedData.find(
				item => item.date === date && item.generatorKey === generatorKey
			);
			return record ? record.consumption : 0;
		}),
	}));

	return {
		series,
		categories,
		generators,
	};
};

const prepareChartDataConsumptionECharts = (data, filter) => {
	const dateFormat = formatMap[filter] || 'DD-MM-YYYY';

	const groupedData = data.reduce((acc, item) => {
		if (
			!item.supply_date ||
			!item.fuel_type_info ||
			!item.unit_measurement_info
		)
			return acc;
		const date = formatDate(item.supply_date, filter);
		const fuelType = item.fuel_type_info?.name || 'Desconocido';
		const unit = item.unit_measurement_info?.name || 'N/A';
		const key = `${date}-${fuelType}-${unit}`;
		const amount = Number(item.amount) || 0;

		if (!acc[key]) {
			acc[key] = {
				date,
				fuelType,
				unit,
				amount: 0,
				extra: {
					tankName: item.tank_name || 'Desconocido',
					barge: item.barge || 'No aplica',
					stock: item.stock || 0,
					capacity: item.capacity || 0,
					fuelType,
					unit,
				},
			};
		}
		acc[key].amount += amount;
		return acc;
	}, {});

	const sortedData = Object.values(groupedData).sort((a, b) =>
		moment(a.date, dateFormat).diff(moment(b.date, dateFormat))
	);
	const categories = [...new Set(sortedData.map(item => item.date))];
	const fuelTypes = [
		...new Set(sortedData.map(item => `${item.fuelType} (${item.unit})`)),
	];

	const series = fuelTypes.map(ft => ({
		name: ft,
		type: 'bar',
		barGap: 0,
		emphasis: {
			focus: 'series',
		},
		data: categories.map(date => {
			const record = sortedData.find(
				item =>
					item.date === date && `${item.fuelType} (${item.unit})` === ft
			);
			return record ? record.amount : 0;
		}),
	}));

	return {
		series,
		categories,
		fuelTypes,
	};
};

const FuelBalancePowerGenerationEChart = ({
	establishmentId,
	title,
	legendPosition = 'bottom',
	option,
	um = '',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedFilter, setSelectedFilter] = useState(option);
	const [selectedGroup, setSelectedGroup] = useState('power');

	// Power data
	const selectorPower = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.hourmeterSummary[type] || null })
	);
	const { info: infoPower } = useSelector(state =>
		selectorPower(state, 'power_consumption')
	);

	// Consumption data
	const selectorConsumption = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.fuelSupplyByEstablishment[type] || null })
	);
	const { info: infoConsumption } = useSelector(state =>
		selectorConsumption(state, 'balance')
	);

	const fetchConsumptionData = useCallback(
		(idEstablishment, filter) => {
			if (selectedGroup === 'power') {
				dispatch(
					getHourMeterSummary({
						establishment_id: idEstablishment,
						filter: filter,
						type: 'power_consumption',
					})
				);
			} else {
				dispatch(
					getConsumptionByEstablishment({
						establishment_id: idEstablishment,
						filter: filter,
						type: 'balance',
					})
				);
			}
		},
		[dispatch, selectedGroup]
	);

	// Fetch data
	useEffect(() => {
		if (!establishmentId) return;
		fetchConsumptionData(establishmentId, selectedFilter);
	}, [establishmentId, selectedFilter, fetchConsumptionData]);

	const { series, categories, legendData } = useMemo(() => {
		if (selectedGroup === 'power' && Array.isArray(infoPower?.data)) {
			const { series, categories, generators } =
				prepareChartDataPowerECharts(infoPower.data, selectedFilter);
			return { series, categories, legendData: generators };
		} else if (
			selectedGroup === 'comsumption' &&
			Array.isArray(infoConsumption?.data)
		) {
			const { series, categories, fuelTypes } =
				prepareChartDataConsumptionECharts(
					infoConsumption.data,
					selectedFilter
				);
			return { series, categories, legendData: fuelTypes };
		}
		return { series: [], categories: [], legendData: [] };
	}, [infoPower?.data, infoConsumption?.data]);

	console.log({
		series,
		categories,
		legendData,
	});

	const yAxisUnit = useMemo(() => {
		if (selectedGroup === 'power') {
			return getUniqueUnits(infoPower?.data);
		} else {
			return getUniqueUnits(infoConsumption?.data);
		}
	}, [infoPower?.data, infoConsumption?.data]);

	const options = useMemo(
		() => ({
			color: ECHARTS_COLOR_PALETTE,
			tooltip: {
				trigger: 'axis',
				axisPointer: {
					type: 'shadow',
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			grid: {
				top: '12%',
				left: '3%',
				right: '4%',
				bottom: '15%',
				containLabel: true,
			},
			xAxis: [
				{
					type: 'category',
					data: categories,
				},
			],
			yAxis: [
				{
					type: 'value',
					name: yAxisUnit,
				},
			],
			series: series,
		}),
		[series, categories, legendData, yAxisUnit]
	);

	const handleFilterChange = filter => {
		setSelectedFilter(filter);
	};

	const handleGroupChange = group => {
		setSelectedGroup(group);
	};

	const noData =
		selectedGroup === 'power'
			? !infoPower?.data?.length
			: !infoConsumption?.data?.length;

	return (
		<Card className='m-0 h-100'>
			<CardHeader className='p-2 align-items-center d-flex'>
				<h5 className='card-title mb-0 flex-grow-1'>{title}</h5>
				<UncontrolledDropdown className='card-header-dropdown me-2'>
					<DropdownToggle
						tag='a'
						className='text-reset dropdown-btn'
						role='button'
					>
						<span className='fs-11'>Agrupado por: </span>
						<span className='text-muted fw-bold text-uppercase'>
							{selectedGroup === 'power'
								? 'Fuente de Generación de Energía'
								: 'Consumo General'}
							<i className='mdi mdi-chevron-down ms-1'></i>
						</span>
					</DropdownToggle>
					<DropdownMenu className='dropdown-menu-end'>
						<DropdownItem
							onClick={() => handleGroupChange('comsumption')}
							active={selectedGroup === 'comsumption'}
						>
							Consumo General
						</DropdownItem>
						<DropdownItem
							onClick={() => handleGroupChange('power')}
							active={selectedGroup === 'power'}
						>
							Fuente de Generación de Energía
						</DropdownItem>
					</DropdownMenu>
				</UncontrolledDropdown>
				<div className='btn-box-dashboard'>
					{['2A', '1A', '6M', '3M', '1M'].map(f => (
						<Button
							key={f}
							color={selectedFilter === f ? 'primary' : 'secondary'}
							size='sm'
							className={`btn-soft-${
								selectedFilter === f ? 'primary' : 'secondary'
							} me-1`}
							onClick={() => handleFilterChange(f)}
						>
							{f}
						</Button>
					))}
				</div>
			</CardHeader>

			{noData ? (
				<>
					<CardBody className='p-2'>
						<div className='d-flex justify-content-center align-items-center p-3'>
							<div className='text-center'>
								<div className='avatar-md'>
									<span className='avatar-title bg-info-subtle rounded-circle fs-1'>
										<i className='ri-gas-station-fill text-info'></i>
									</span>
								</div>
							</div>
						</div>
					</CardBody>
					<CardFooter className='m-0 p-2 text-center'>
						<h6 className='mb-0 p-0 text-wrap w-100'>
							<span className='fs-11 badge bg-info-subtle text-info text-wrap'>
								No cuenta con registro de consumo de combustible
							</span>
						</h6>
					</CardFooter>
				</>
			) : (
				<CardBody className='p-0'>
					<EChartsBarChart
						options={options}
						minHeight={minHeight}
						maxHeight={maxHeight}
					/>
				</CardBody>
			)}
		</Card>
	);
};

export default FuelBalancePowerGenerationEChart;
